import type {
  AdminDeviceKpi,
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminFailedFlow,
  AdminProjectionFreshness,
  AdminQueryMeta,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  formatAdminAccountIdentityLabel,
  formatAdminLinkedProviderCountLabel,
  formatAdminProviderIdentityLabel,
} from "../../shared/formatting/format-admin-account-identity";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import {
  formatAdminBooleanLabel,
  formatAdminChatMembershipCountBucketLabel,
  formatAdminChatMessageLengthBucketLabel,
  formatAdminChatRoomKindLabel,
  formatAdminCarePlanDosePresenceLabel,
  formatAdminCareOccurrenceResultKindLabel,
  formatAdminCarePlanRecurrenceBucketLabel,
  formatAdminCareOccurrenceSnoozeWindowBucketLabel,
  formatAdminPetAttemptCountBucketLabel,
  formatAdminPetBreedSelectionBucketLabel,
  formatAdminPetProfileCompletionStepLabel,
  formatAdminPetSpeciesBucketLabel,
  formatAdminReceiptCategoryMutationTypeLabel,
  formatAdminReceiptExtractSourceBucketLabel,
  formatAdminReceiptImageByteSizeBucketLabel,
  formatAdminReceiptExtractionConfidenceBucketLabel,
  formatAdminRideLongestAddressLengthBucketLabel,
  formatAdminRideVisitLeadTimeBucketLabel,
  formatAdminSocialProfileMutationFieldSetBucketLabel,
  formatAdminSocialPublicPetCountBucketLabel,
  formatAdminSocialTargetUserLookupKindLabel,
  formatAdminTelemetryAppBuildNumberLabel,
  formatAdminTelemetryDeviceModelBucketLabel,
  formatAdminTelemetryDeviceModelNameBucketLabel,
  formatAdminTelemetryLocaleLabel,
  formatAdminTelemetryNetworkTypeBucketLabel,
  formatAdminTelemetryOsMajorBucketLabel,
  formatAdminTelemetryOsVersionMinorBucketLabel,
  formatAdminTelemetryPhysicalDeviceBucketLabel,
  formatAdminTelemetryRuntimeSummaryLabel,
  formatAdminTelemetryTimezoneNameBucketLabel,
  formatAdminTelemetryTimezoneOffsetBucketLabel,
} from "../../shared/formatting/format-admin-runtime-context";
import { formatAdminSourceKindLabel } from "../../shared/formatting/format-admin-source-kind";
import {
  buildDeviceKpiDrilldownTarget,
  type AdminDeviceKpiDrilldownTarget,
} from "./device-kpis-use-cases";
import { buildFailedFlowDrilldownTarget } from "../failed-flows/failed-flows-use-cases";

export type DeviceKpiRowViewModel = {
  id: string;
  deviceInstallId: string;
  platform?: string;
  appVersion?: string;
  release?: string;
  lastSeenAt?: string;
  sessionStartCount: number;
  failureCount: number;
  deviceInstallIdLabel: string;
  platformLabel: string;
  appVersionLabel: string;
  releaseLabel: string;
  lastSeenAtLabel: string;
  sessionStartCountLabel: string;
  authSuccessCountLabel: string;
  petSuccessCountLabel: string;
  todayStatusSaveSuccessCountLabel: string;
  dailySpeechCaptureStartCountLabel: string;
  dailySpeechCaptureCancelCountLabel: string;
  dailyFlowSuccessCountLabel: string;
  healthFlowSuccessCountLabel: string;
  healthRecordSaveSuccessCountLabel: string;
  receiptFlowCountLabel: string;
  receiptSaveSuccessCountLabel: string;
  socialSuccessCountLabel: string;
  chatSuccessCountLabel: string;
  communitySuccessCountLabel: string;
  carePlanSuccessCountLabel: string;
  rideRequestSuccessCountLabel: string;
  totalSuccessCountLabel: string;
  failureCountLabel: string;
  topErrorCodeLabel: string;
  latestUserIdHashLabel: string;
  latestUserAccountIdentityLabel: string;
  latestUserProviderIdentityLabel: string;
  latestUserLinkedProviderCountLabel: string;
  latestRuntimeSummaryLabel: string;
  drilldownTarget: AdminDeviceKpiDrilldownTarget;
  detail: DeviceKpiDetailViewModel;
};

export type DeviceKpiScenarioPresetId =
  | "auth"
  | "pet"
  | "social"
  | "chat"
  | "community"
  | "daily"
  | "health"
  | "receipt"
  | "medication"
  | "ride"
  | "deletion";

export type DeviceKpiScenarioPresetViewModel = {
  id: DeviceKpiScenarioPresetId;
  title: string;
  headline: string;
  summary: string;
  metricLabel: string;
  metricValueLabel: string;
  sourceKeys: string[];
  sourceSummary: string;
  semantics: string;
  failedFlowsTo?: string;
  userSummaryTo?: string;
  releaseRegressionTo?: string;
};

export type DeviceKpiContextCueViewModel = {
  speechDurationBucketLabel: string;
  speechDurationBucketObservedAtLabel: string;
  draftEditCountBucketLabel: string;
  draftEditCountBucketObservedAtLabel: string;
  draftDiscardReasonCategoryLabel: string;
  draftDiscardReasonCategoryObservedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiPetContextCueViewModel = {
  profileCompletionStepLabel: string;
  attemptCountBucketLabel: string;
  speciesBucketLabel: string;
  breedSelectionBucketLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiSocialContextCueViewModel = {
  targetUserLookupKindLabel: string;
  publicPetCountBucketLabel: string;
  profileMutationFieldSetBucketLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiChatContextCueViewModel = {
  roomKindLabel: string;
  messageLengthBucketLabel: string;
  membershipCountBucketLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiReceiptContextCueViewModel = {
  extractSourceBucketLabel: string;
  imageByteSizeBucketLabel: string;
  extractionConfidenceBucketLabel: string;
  manualOverrideLabel: string;
  categoryMutationTypeLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiCarePlanContextCueViewModel = {
  scheduleRecurrenceBucketLabel: string;
  dosePresenceLabel: string;
  occurrenceActionKindLabel: string;
  snoozeWindowBucketLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpiRideContextCueViewModel = {
  visitLeadTimeBucketLabel: string;
  longestAddressLengthBucketLabel: string;
  memoProvidedLabel: string;
  observedAtLabel: string;
  hasAnyContext: boolean;
};

export type DeviceKpisViewModel = {
  totalCountLabel: string;
  generatedAtLabel: string;
  requestId: string;
  nextCursor?: string;
  selectedScopeLabel: string;
  baselineObservedAtLabel: string;
  latestObservedAtLabel: string;
  latestSourceActivityAtLabel: string;
  latestObservedLagging: boolean;
  observationLagLabel: string;
  freshnessAlignment: "aligned" | "mismatch";
  distinctUserCountLabel: string;
  totalFailureCountLabel: string;
  totalSessionCountLabel: string;
  projectionGapRowCountLabel: string;
  projectionGapEvidence?: DeviceKpiProjectionGapEvidenceViewModel;
  projectionGapRows: DeviceKpiProjectionGapRowViewModel[];
  rows: DeviceKpiRowViewModel[];
  sourceFreshness: Array<{
    source: string;
    freshness: AdminProjectionFreshness;
    sourceKind?: string;
    sourceKindLabel?: string;
  }>;
};

export type DeviceKpiProjectionGapEvidenceViewModel = {
  latestFailedFlowAtLabel: string;
  requestId?: string;
  errorCodeLabel: string;
  deviceInstallIdLabel: string;
  releaseLabel: string;
  appVersionLabel: string;
  platformLabel: string;
};

export type DeviceKpiProjectionGapRowViewModel = DeviceKpiProjectionGapEvidenceViewModel & {
  id: string;
  latestFailedFlowAt: string;
  failedFlowsTo?: string;
};

export type DeviceKpiDetailViewModel = {
  deviceInstallId: string;
  deviceInstallIdLabel: string;
  platformLabel: string;
  appVersionLabel: string;
  releaseLabel: string;
  lastSeenAtLabel: string;
  latestUserIdHashLabel: string;
  latestUserAccountIdentityLabel: string;
  latestUserProviderIdentityLabel: string;
  latestUserLinkedProviderCountLabel: string;
  latestRuntimeAppBuildNumberLabel: string;
  latestRuntimeLocaleLabel: string;
  latestRuntimeTimezoneOffsetBucketLabel: string;
  latestRuntimeTimezoneNameBucketLabel: string;
  latestRuntimeDeviceModelBucketLabel: string;
  latestRuntimeDeviceModelNameBucketLabel: string;
  latestRuntimeOsMajorBucketLabel: string;
  latestRuntimeOsVersionMinorBucketLabel: string;
  latestRuntimeNetworkTypeBucketLabel: string;
  latestRuntimePhysicalDeviceBucketLabel: string;
  latestRuntimeSummaryLabel: string;
  latestRuntimeContextAtLabel: string;
  sessionStartCountLabel: string;
  authSuccessCountLabel: string;
  petCreateSuccessCountLabel: string;
  petUpdateSuccessCountLabel: string;
  petDeleteSuccessCountLabel: string;
  petPhotoConfirmSuccessCountLabel: string;
  petSuccessCountLabel: string;
  todayStatusSaveSuccessCountLabel: string;
  dailySpeechCaptureStartCountLabel: string;
  dailySpeechCaptureCancelCountLabel: string;
  dailyDraftCreateSuccessCountLabel: string;
  dailyDraftUpdateCountLabel: string;
  dailyDraftDiscardCountLabel: string;
  dailyRecordSaveSuccessCountLabel: string;
  dailyMediaUpdateSuccessCountLabel: string;
  dailyFlowSuccessCountLabel: string;
  healthFileUploadSuccessCountLabel: string;
  healthFileConfirmSuccessCountLabel: string;
  healthAnalysisStartCountLabel: string;
  healthFlowSuccessCountLabel: string;
  healthRecordSaveSuccessCountLabel: string;
  receiptExtractCountLabel: string;
  receiptSaveSuccessCountLabel: string;
  receiptCategoryCreateSuccessCountLabel: string;
  receiptCategoryUpdateSuccessCountLabel: string;
  receiptCategoryDeleteSuccessCountLabel: string;
  receiptFlowCountLabel: string;
  socialSuccessCountLabel: string;
  chatSuccessCountLabel: string;
  communitySuccessCountLabel: string;
  carePlanSuccessCountLabel: string;
  rideRequestSuccessCountLabel: string;
  totalSuccessCountLabel: string;
  failureCountLabel: string;
  topErrorCodeLabel: string;
  dailyContextCue: DeviceKpiContextCueViewModel;
  petContextCue: DeviceKpiPetContextCueViewModel;
  socialContextCue: DeviceKpiSocialContextCueViewModel;
  chatContextCue: DeviceKpiChatContextCueViewModel;
  receiptContextCue: DeviceKpiReceiptContextCueViewModel;
  carePlanContextCue: DeviceKpiCarePlanContextCueViewModel;
  rideContextCue: DeviceKpiRideContextCueViewModel;
  drilldownTarget: AdminDeviceKpiDrilldownTarget;
  scenarioPresets: DeviceKpiScenarioPresetViewModel[];
};

function toPlatformLabel(value: AdminDeviceKpi["platform"]): string {
  return value.toUpperCase();
}

function toLabel(value: string | undefined): string {
  return value ?? "-";
}

function toDeviceInstallIdLabel(value: string): string {
  if (value.length <= 14) {
    return value;
  }

  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function toOptionalDateLabel(value: string | undefined): string {
  return value ? formatAdminDate(value) : "-";
}

const DEVICE_KPI_OBSERVATION_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

function formatObservationLagLabel(
  baselineObservedAt: string | undefined,
  generatedAt: string,
  locale: AdminDashboardLocale,
): string {
  const latestObservedTimestamp = toTimestamp(baselineObservedAt);
  const generatedTimestamp = toTimestamp(generatedAt);

  if (latestObservedTimestamp === undefined || generatedTimestamp === undefined) {
    return "-";
  }

  const lagMs = generatedTimestamp - latestObservedTimestamp;

  if (lagMs <= DEVICE_KPI_OBSERVATION_STALE_THRESHOLD_MS) {
    return "-";
  }

  const totalMinutes = Math.floor(lagMs / 60_000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (locale === "ko") {
    if (days > 0) {
      return hours > 0 ? `${days}일 ${hours}시간` : `${days}일`;
    }

    return hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`;
  }

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function toTimestamp(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function isDeviceKpiObservationLagging(
  latestObservedAt: string | undefined,
  generatedAt: string,
): boolean {
  const latestObservedTimestamp = toTimestamp(latestObservedAt);
  const generatedTimestamp = toTimestamp(generatedAt);

  if (latestObservedTimestamp === undefined || generatedTimestamp === undefined) {
    return false;
  }

  return generatedTimestamp - latestObservedTimestamp > DEVICE_KPI_OBSERVATION_STALE_THRESHOLD_MS;
}

function deriveFreshnessAlignment(
  baselineObservedLagging: boolean,
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "aligned" | "mismatch" {
  if (!baselineObservedLagging) {
    return "aligned";
  }

  return Object.values(sourceFreshness).includes("fresh") ? "mismatch" : "aligned";
}

function compareDeviceKpisByLastSeenAt(
  left: AdminDeviceKpi,
  right: AdminDeviceKpi,
): number {
  const leftLastSeenAt = Date.parse(left.lastSeenAt);
  const rightLastSeenAt = Date.parse(right.lastSeenAt);

  if (!Number.isNaN(leftLastSeenAt) && !Number.isNaN(rightLastSeenAt) && rightLastSeenAt !== leftLastSeenAt) {
    return rightLastSeenAt - leftLastSeenAt;
  }

  if (!Number.isNaN(leftLastSeenAt) && Number.isNaN(rightLastSeenAt)) {
    return -1;
  }

  if (Number.isNaN(leftLastSeenAt) && !Number.isNaN(rightLastSeenAt)) {
    return 1;
  }

  return left.deviceInstallId.localeCompare(right.deviceInstallId);
}

export function pickLatestDeviceKpiObservedAt(items: AdminDeviceKpi[]): string | undefined {
  return [...items].sort(compareDeviceKpisByLastSeenAt)[0]?.lastSeenAt;
}

function summarizeTotalSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return (
    deviceKpi.sessionStartCount +
    deviceKpi.authSuccessCount +
    deviceKpi.petCreateSuccessCount +
    deviceKpi.petUpdateSuccessCount +
    deviceKpi.petDeleteSuccessCount +
    deviceKpi.petPhotoConfirmSuccessCount +
    deviceKpi.todayStatusSaveSuccessCount +
    deviceKpi.dailySpeechCaptureStartCount +
    deviceKpi.dailySpeechCaptureCancelCount +
    deviceKpi.dailyDraftCreateSuccessCount +
    deviceKpi.dailyDraftUpdateCount +
    deviceKpi.dailyDraftDiscardCount +
    deviceKpi.dailyRecordSaveSuccessCount +
    deviceKpi.dailyMediaUpdateSuccessCount +
    deviceKpi.healthFileUploadSuccessCount +
    deviceKpi.healthFileConfirmSuccessCount +
    deviceKpi.healthAnalysisStartCount +
    deviceKpi.healthRecordSaveSuccessCount +
    deviceKpi.receiptExtractCount +
    deviceKpi.receiptSaveSuccessCount +
    deviceKpi.receiptCategoryCreateSuccessCount +
    deviceKpi.receiptCategoryUpdateSuccessCount +
    deviceKpi.receiptCategoryDeleteSuccessCount +
    deviceKpi.socialSuccessCount +
    deviceKpi.chatSuccessCount +
    deviceKpi.communitySuccessCount +
    deviceKpi.carePlanSuccessCount +
    deviceKpi.rideRequestSuccessCount
  );
}

function summarizePetSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return (
    deviceKpi.petCreateSuccessCount +
    deviceKpi.petUpdateSuccessCount +
    deviceKpi.petDeleteSuccessCount +
    deviceKpi.petPhotoConfirmSuccessCount
  );
}

function summarizeDailyFlowSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return (
    deviceKpi.dailySpeechCaptureStartCount +
    deviceKpi.dailySpeechCaptureCancelCount +
    deviceKpi.dailyDraftCreateSuccessCount +
    deviceKpi.dailyDraftUpdateCount +
    deviceKpi.dailyDraftDiscardCount +
    deviceKpi.dailyRecordSaveSuccessCount +
    deviceKpi.dailyMediaUpdateSuccessCount
  );
}

function summarizeHealthFlowSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return (
    deviceKpi.healthFileUploadSuccessCount +
    deviceKpi.healthFileConfirmSuccessCount +
    deviceKpi.healthAnalysisStartCount +
    deviceKpi.healthRecordSaveSuccessCount
  );
}

function summarizeReceiptFlowCount(deviceKpi: AdminDeviceKpi): number {
  return (
    deviceKpi.receiptExtractCount +
    deviceKpi.receiptSaveSuccessCount +
    deviceKpi.receiptCategoryCreateSuccessCount +
    deviceKpi.receiptCategoryUpdateSuccessCount +
    deviceKpi.receiptCategoryDeleteSuccessCount
  );
}

function summarizeSocialSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return deviceKpi.socialSuccessCount;
}

function summarizeChatSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return deviceKpi.chatSuccessCount;
}

function summarizeCommunitySuccessCount(deviceKpi: AdminDeviceKpi): number {
  return deviceKpi.communitySuccessCount;
}

function summarizeCarePlanSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return deviceKpi.carePlanSuccessCount;
}

function summarizeRideSuccessCount(deviceKpi: AdminDeviceKpi): number {
  return deviceKpi.rideRequestSuccessCount;
}

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return pickAdminDashboardLocaleText(locale, { en, ko });
}

function createDeviceKpiScenarioPresets(
  deviceKpi: AdminDeviceKpi,
  drilldownTarget: AdminDeviceKpiDrilldownTarget,
  locale: AdminDashboardLocale,
): DeviceKpiScenarioPresetViewModel[] {
  const dailyFlowSuccessCount = summarizeDailyFlowSuccessCount(deviceKpi);
  const healthFlowSuccessCount = summarizeHealthFlowSuccessCount(deviceKpi);
  const petSuccessCount = summarizePetSuccessCount(deviceKpi);
  const receiptFlowCount = summarizeReceiptFlowCount(deviceKpi);
  const socialSuccessCount = summarizeSocialSuccessCount(deviceKpi);
  const chatSuccessCount = summarizeChatSuccessCount(deviceKpi);
  const communitySuccessCount = summarizeCommunitySuccessCount(deviceKpi);
  const carePlanSuccessCount = summarizeCarePlanSuccessCount(deviceKpi);
  const rideSuccessCount = summarizeRideSuccessCount(deviceKpi);

  return [
    {
      id: "auth",
      title: text(locale, "Auth / Session", "인증 / 세션"),
      headline:
        deviceKpi.authSuccessCount > 0
          ? `${formatAdminCount(deviceKpi.authSuccessCount)} ${text(locale, "auth success", "인증 성공")}`
          : text(locale, "Session-only or no auth success", "세션만 있거나 인증 성공이 없습니다"),
      summary: `${text(locale, "session", "세션")} ${formatAdminCount(deviceKpi.sessionStartCount)} / ${text(locale, "auth", "인증")} ${formatAdminCount(deviceKpi.authSuccessCount)}`,
      metricLabel: text(locale, "Auth success", "인증 성공"),
      metricValueLabel: formatAdminCount(deviceKpi.authSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Session starts count successful GET /api/auth/session. Auth success counts successful POST /api/auth/social-login only.",
        "세션 시작은 성공한 GET /api/auth/session만 집계합니다. 인증 성공은 성공한 POST /api/auth/social-login만 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "pet",
      title: text(locale, "Pet Onboarding / Profile", "펫 등록 / 프로필"),
      headline:
        petSuccessCount > 0
          ? `${formatAdminCount(petSuccessCount)} ${text(locale, "pet flow success", "펫 흐름 성공")}`
          : text(locale, "No pet flow success in this bucket", "이 버킷에는 펫 흐름 성공이 없습니다"),
      summary: `${text(locale, "create", "등록")} ${formatAdminCount(deviceKpi.petCreateSuccessCount)} / ${text(locale, "update", "수정")} ${formatAdminCount(deviceKpi.petUpdateSuccessCount)} / ${text(locale, "delete", "삭제")} ${formatAdminCount(deviceKpi.petDeleteSuccessCount)} / ${text(locale, "photo", "사진")} ${formatAdminCount(deviceKpi.petPhotoConfirmSuccessCount)}`,
      metricLabel: text(locale, "Pet flow total", "펫 흐름 합계"),
      metricValueLabel: formatAdminCount(petSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Pet counters come only from successful request outcomes for pet create, update, delete, and photo confirm.",
        "펫 카운터는 pet create, update, delete, photo confirm의 성공한 request outcome만 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "social",
      title: text(locale, "Social Profile / Follow", "소셜 프로필 / 팔로우"),
      headline:
        socialSuccessCount > 0
          ? `${formatAdminCount(socialSuccessCount)} ${text(locale, "social success", "소셜 성공")}`
          : text(locale, "No social success in this bucket", "이 버킷에는 소셜 성공이 없습니다"),
      summary: `${text(locale, "successful social requests", "성공한 소셜 요청")} ${formatAdminCount(socialSuccessCount)}`,
      metricLabel: text(locale, "Social success total", "소셜 성공 합계"),
      metricValueLabel: formatAdminCount(socialSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Social KPI counts successful social profile and follow request outcomes in the same install bucket.",
        "소셜 KPI는 같은 설치본 버킷에서 성공한 social profile 및 follow request outcome을 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "chat",
      title: text(locale, "Chat", "채팅"),
      headline:
        chatSuccessCount > 0
          ? `${formatAdminCount(chatSuccessCount)} ${text(locale, "chat success", "채팅 성공")}`
          : text(locale, "No chat success in this bucket", "이 버킷에는 채팅 성공이 없습니다"),
      summary: `${text(locale, "successful chat requests", "성공한 채팅 요청")} ${formatAdminCount(chatSuccessCount)}`,
      metricLabel: text(locale, "Chat success total", "채팅 성공 합계"),
      metricValueLabel: formatAdminCount(chatSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Chat KPI counts successful direct and group room or message request outcomes in the same install bucket.",
        "채팅 KPI는 같은 설치본 버킷에서 성공한 direct/group room 및 message request outcome을 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "community",
      title: text(locale, "Community Posting", "커뮤니티 게시"),
      headline:
        communitySuccessCount > 0
          ? `${formatAdminCount(communitySuccessCount)} ${text(locale, "community success", "커뮤니티 성공")}`
          : text(locale, "No community success in this bucket", "이 버킷에는 커뮤니티 성공이 없습니다"),
      summary: `${text(locale, "successful community requests", "성공한 커뮤니티 요청")} ${formatAdminCount(communitySuccessCount)}`,
      metricLabel: text(locale, "Community success total", "커뮤니티 성공 합계"),
      metricValueLabel: formatAdminCount(communitySuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Community KPI counts successful image upload, post mutation, and comment request outcomes in the same install bucket.",
        "커뮤니티 KPI는 같은 설치본 버킷에서 성공한 이미지 업로드, 게시물 변경, 댓글 request outcome을 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "daily",
      title: text(locale, "Daily Records", "일상 기록"),
      headline:
        dailyFlowSuccessCount > 0
          ? `${formatAdminCount(dailyFlowSuccessCount)} ${text(locale, "daily flow signal", "일상 흐름 신호")}`
          : text(locale, "No daily flow signal in this bucket", "이 버킷에는 일상 흐름 신호가 없습니다"),
      summary: `${text(locale, "today", "오늘 상태")} ${formatAdminCount(deviceKpi.todayStatusSaveSuccessCount)} / ${text(locale, "speech", "음성")} ${formatAdminCount(deviceKpi.dailySpeechCaptureStartCount + deviceKpi.dailySpeechCaptureCancelCount)} / ${text(locale, "draft", "초안")} ${formatAdminCount(deviceKpi.dailyDraftCreateSuccessCount + deviceKpi.dailyDraftUpdateCount + deviceKpi.dailyDraftDiscardCount)} / ${text(locale, "save", "저장")} ${formatAdminCount(deviceKpi.dailyRecordSaveSuccessCount + deviceKpi.dailyMediaUpdateSuccessCount)}`,
      metricLabel: text(locale, "Daily flow total", "일상 흐름 합계"),
      metricValueLabel: formatAdminCount(dailyFlowSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes", "client_flow_events"],
      sourceSummary: "request_outcome + client_flow_event + projection",
      semantics: text(
        locale,
        "Today status, draft create, daily save, and media update come from request outcomes. Speech start/cancel and draft update/discard come from client_flow_events.",
        "today status, draft create, daily save, media update는 request outcome에서 집계합니다. speech start 또는 cancel과 draft update 또는 discard는 client_flow_events에서 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "health",
      title: text(locale, "Health Records", "건강 기록"),
      headline:
        healthFlowSuccessCount > 0
          ? `${formatAdminCount(healthFlowSuccessCount)} ${text(locale, "health flow signal", "건강 흐름 신호")}`
          : text(locale, "No health flow signal in this bucket", "이 버킷에는 건강 흐름 신호가 없습니다"),
      summary: `${text(locale, "upload", "업로드")} ${formatAdminCount(deviceKpi.healthFileUploadSuccessCount)} / ${text(locale, "confirm", "확정")} ${formatAdminCount(deviceKpi.healthFileConfirmSuccessCount)} / ${text(locale, "analysis", "분석")} ${formatAdminCount(deviceKpi.healthAnalysisStartCount)} / ${text(locale, "save", "저장")} ${formatAdminCount(deviceKpi.healthRecordSaveSuccessCount)}`,
      metricLabel: text(locale, "Health flow total", "건강 흐름 합계"),
      metricValueLabel: formatAdminCount(healthFlowSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Health analysis start includes succeeded and partial request outcomes so queued or degraded analysis entry still appears in install-level KPI.",
        "health analysis start는 succeeded와 partial request outcome을 함께 포함하므로 대기열 상태이거나 품질이 낮아진 분석 진입도 설치본 KPI에 남습니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "receipt",
      title: text(locale, "Receipts", "영수증"),
      headline:
        receiptFlowCount > 0
          ? `${formatAdminCount(receiptFlowCount)} ${text(locale, "receipt flow signal", "영수증 흐름 신호")}`
          : text(locale, "No receipt flow signal in this bucket", "이 버킷에는 영수증 흐름 신호가 없습니다"),
      summary: `${text(locale, "extract", "추출")} ${formatAdminCount(deviceKpi.receiptExtractCount)} / ${text(locale, "save", "저장")} ${formatAdminCount(deviceKpi.receiptSaveSuccessCount)} / ${text(locale, "category", "카테고리")} ${formatAdminCount(deviceKpi.receiptCategoryCreateSuccessCount + deviceKpi.receiptCategoryUpdateSuccessCount + deviceKpi.receiptCategoryDeleteSuccessCount)}`,
      metricLabel: text(locale, "Receipt flow total", "영수증 흐름 합계"),
      metricValueLabel: formatAdminCount(receiptFlowCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Receipt extract counts succeeded and partial extraction responses. Explicit save and category create/update/delete remain separate counters.",
        "receipt extract는 succeeded와 partial 추출 응답을 함께 집계합니다. explicit save와 category create 또는 update 또는 delete는 별도 카운터로 유지합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "medication",
      title: text(locale, "CarePlan", "케어플랜"),
      headline:
        carePlanSuccessCount > 0
          ? `${formatAdminCount(carePlanSuccessCount)} ${text(locale, "medication success", "복약 성공")}`
          : text(locale, "No medication success in this bucket", "이 버킷에는 복약 성공이 없습니다"),
      summary: `${text(locale, "successful medication requests", "성공한 복약 요청")} ${formatAdminCount(carePlanSuccessCount)}`,
      metricLabel: text(locale, "CarePlan success total", "복약 성공 합계"),
      metricValueLabel: formatAdminCount(carePlanSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "CarePlan KPI counts successful medication list, detail, schedule mutation, and occurrence action request outcomes in the same install bucket.",
        "복약 KPI는 같은 설치본 버킷에서 성공한 medication list, detail, schedule 변경, occurrence action request outcome을 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "ride",
      title: text(locale, "Ride Requests", "이동지원 요청"),
      headline:
        rideSuccessCount > 0
          ? `${formatAdminCount(rideSuccessCount)} ${text(locale, "ride success", "이동지원 성공")}`
          : text(locale, "No ride success in this bucket", "이 버킷에는 이동지원 성공이 없습니다"),
      summary: `${text(locale, "successful ride requests", "성공한 이동지원 요청")} ${formatAdminCount(rideSuccessCount)}`,
      metricLabel: text(locale, "Ride success total", "이동지원 성공 합계"),
      metricValueLabel: formatAdminCount(rideSuccessCount),
      sourceKeys: ["admin_device_kpi_daily", "admin_request_outcomes"],
      sourceSummary: "request_outcome + projection",
      semantics: text(
        locale,
        "Ride KPI counts successful ride request list, detail, create, update, delete, submit, and cancel request outcomes in the same install bucket.",
        "이동지원 KPI는 같은 설치본 버킷에서 성공한 ride request list, detail, create, update, delete, submit, cancel request outcome을 집계합니다.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
    {
      id: "deletion",
      title: text(locale, "Account Deletion Follow-up", "계정 삭제 후속 조사"),
      headline:
        deviceKpi.failureCount > 0
          ? `${formatAdminCount(deviceKpi.failureCount)} ${text(locale, "failure to inspect", "조사할 실패")}`
          : text(locale, "Use this install scope for deletion follow-up", "이 설치본 축을 계정 삭제 후속 조사에 사용하세요"),
      summary: `${text(locale, "failures", "실패")} ${formatAdminCount(deviceKpi.failureCount)} / ${text(locale, "top error", "상위 오류")} ${deviceKpi.topErrorCode ?? "-"}`,
      metricLabel: text(locale, "Failure count", "실패 수"),
      metricValueLabel: formatAdminCount(deviceKpi.failureCount),
      sourceKeys: ["admin_request_outcomes"],
      sourceSummary: "request_outcome",
      semantics: text(
        locale,
        "No dedicated install-level success KPI exists for account deletion here. Use the same install scope to pivot into failed flows and release regression for follow-up.",
        "여기에는 계정 삭제 전용 설치본 성공 KPI가 없습니다. 같은 설치본 범위에서 오류 흐름과 릴리스 회귀 화면으로 전환해 후속 조사하세요.",
      ),
      failedFlowsTo: drilldownTarget.failedFlows,
      releaseRegressionTo: drilldownTarget.releaseRegression,
      userSummaryTo: drilldownTarget.userSummary,
    },
  ];
}

export function pickDefaultDeviceKpiScenarioPresetId(
  detail: Pick<
    DeviceKpiDetailViewModel,
    | "socialSuccessCountLabel"
    | "chatSuccessCountLabel"
    | "communitySuccessCountLabel"
    | "dailyFlowSuccessCountLabel"
    | "receiptFlowCountLabel"
    | "healthFlowSuccessCountLabel"
    | "carePlanSuccessCountLabel"
    | "rideRequestSuccessCountLabel"
    | "petSuccessCountLabel"
    | "authSuccessCountLabel"
    | "failureCountLabel"
  >,
): DeviceKpiScenarioPresetId {
  const entries: Array<[DeviceKpiScenarioPresetId, number]> = [
    ["daily", Number.parseInt(detail.dailyFlowSuccessCountLabel, 10)],
    ["receipt", Number.parseInt(detail.receiptFlowCountLabel, 10)],
    ["health", Number.parseInt(detail.healthFlowSuccessCountLabel, 10)],
    ["pet", Number.parseInt(detail.petSuccessCountLabel, 10)],
    ["auth", Number.parseInt(detail.authSuccessCountLabel, 10)],
    ["social", Number.parseInt(detail.socialSuccessCountLabel, 10)],
    ["chat", Number.parseInt(detail.chatSuccessCountLabel, 10)],
    ["community", Number.parseInt(detail.communitySuccessCountLabel, 10)],
    ["medication", Number.parseInt(detail.carePlanSuccessCountLabel, 10)],
    ["ride", Number.parseInt(detail.rideRequestSuccessCountLabel, 10)],
    ["deletion", Number.parseInt(detail.failureCountLabel, 10)],
  ];

  const matched = entries.find(([, value]) => Number.isFinite(value) && value > 0);
  return matched?.[0] ?? "daily";
}

function buildSelectedScopeLabel(rows: AdminDeviceKpi[], locale: AdminDashboardLocale): string {
  const first = rows[0];

  if (!first) {
    return text(locale, "latest device KPI rows", "최신 실기기 지표 행");
  }

  if (first.deviceInstallId) {
    return `${text(locale, "device", "기기")} ${toDeviceInstallIdLabel(first.deviceInstallId)}`;
  }

  if (first.release || first.appVersion) {
    return [first.release, first.appVersion].filter(Boolean).join(" / ");
  }

  return text(locale, "latest device KPI rows", "최신 실기기 지표 행");
}

function countDistinctUsers(values: Array<string | undefined>): string {
  return formatAdminCount(new Set(values.filter((value): value is string => Boolean(value))).size);
}

function buildSelectedScopeLabelFromQuery(
  query: AdminDeviceKpiQuery | undefined,
  locale: AdminDashboardLocale,
): string {
  if (query?.deviceInstallId) {
    return locale === "en"
      ? `device ${query.deviceInstallId}`
      : `기기 ${query.deviceInstallId}`;
  }

  if (query?.release || query?.appVersion || query?.platform) {
    return [query.release, query.appVersion, query.platform].filter(Boolean).join(" / ");
  }

  return pickAdminDashboardLocaleText(locale, {
    en: "latest device KPI rows",
    ko: "최신 실기기 지표 행",
  });
}

export function createEmptyDeviceKpisViewModel(
  meta: AdminQueryMeta,
  query: AdminDeviceKpiQuery | undefined,
  locale: AdminDashboardLocale = "ko",
  projectionGapEvidence?: DeviceKpiProjectionGapEvidenceViewModel,
  projectionGapRows: DeviceKpiProjectionGapRowViewModel[] = [],
  baselineObservedAt?: string,
  latestSourceActivityAtOverride?: string,
): DeviceKpisViewModel {
  const latestSourceActivityAt = latestSourceActivityAtOverride ?? projectionGapRows[0]?.latestFailedFlowAt;
  const latestObservedLagging = false;
  const baselineObservedLagging = isDeviceKpiObservationLagging(baselineObservedAt, meta.generatedAt);
  return {
    totalCountLabel: formatAdminCount(0),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    nextCursor: meta.nextCursor,
    selectedScopeLabel: buildSelectedScopeLabelFromQuery(query, locale),
    baselineObservedAtLabel: toOptionalDateLabel(baselineObservedAt),
    latestObservedAtLabel: "-",
    latestSourceActivityAtLabel: toOptionalDateLabel(latestSourceActivityAt),
    latestObservedLagging,
    observationLagLabel: formatObservationLagLabel(baselineObservedAt, meta.generatedAt, locale),
    freshnessAlignment: deriveFreshnessAlignment(baselineObservedLagging, meta.sourceFreshness),
    distinctUserCountLabel: formatAdminCount(0),
    totalFailureCountLabel: formatAdminCount(0),
    totalSessionCountLabel: formatAdminCount(0),
    projectionGapRowCountLabel: formatAdminCount(projectionGapRows.length),
    projectionGapEvidence,
    projectionGapRows,
    rows: [],
    sourceFreshness: Object.entries(meta.sourceFreshness)
      .sort(([leftSource], [rightSource]) => leftSource.localeCompare(rightSource))
      .map(([source, freshness]) => ({
        source,
        freshness,
        sourceKind: meta.sourceKinds?.[source],
        sourceKindLabel: formatAdminSourceKindLabel(meta.sourceKinds?.[source], locale),
      })),
  };
}

export function toDeviceKpiProjectionGapRowViewModel(
  failedFlow: AdminFailedFlow,
  locale: AdminDashboardLocale = "ko",
): DeviceKpiProjectionGapRowViewModel {
  const failedFlowDrilldownTarget = buildFailedFlowDrilldownTarget(failedFlow);
  return {
    id: [
      failedFlow.requestId ?? "no-request",
      failedFlow.deviceInstallId ?? "no-device",
      failedFlow.createdAt,
    ].join(":"),
    latestFailedFlowAt: failedFlow.createdAt,
    latestFailedFlowAtLabel: formatAdminDate(failedFlow.createdAt),
    requestId: failedFlow.requestId,
    errorCodeLabel: failedFlow.errorCode,
    deviceInstallIdLabel: failedFlow.deviceInstallId ?? "-",
    releaseLabel: toLabel(failedFlow.release),
    appVersionLabel: toLabel(failedFlow.appVersion),
    platformLabel: toPlatformLabel(failedFlow.platform ?? "unknown"),
    failedFlowsTo:
      failedFlowDrilldownTarget.request ??
      failedFlowDrilldownTarget.deviceErrors ??
      failedFlowDrilldownTarget.releaseRegression,
  };
}

export function toDeviceKpiProjectionGapEvidenceViewModel(
  failedFlow: AdminFailedFlow,
  locale: AdminDashboardLocale = "ko",
): DeviceKpiProjectionGapEvidenceViewModel {
  const row = toDeviceKpiProjectionGapRowViewModel(failedFlow, locale);
  return {
    latestFailedFlowAtLabel: row.latestFailedFlowAtLabel,
    requestId: row.requestId,
    errorCodeLabel: row.errorCodeLabel,
    deviceInstallIdLabel: row.deviceInstallIdLabel,
    releaseLabel: row.releaseLabel,
    appVersionLabel: row.appVersionLabel,
    platformLabel: row.platformLabel,
  };
}

export function toDeviceKpiDetailViewModel(
  deviceKpi: AdminDeviceKpi,
  locale: AdminDashboardLocale = "ko",
): DeviceKpiDetailViewModel {
  const totalSuccessCount = summarizeTotalSuccessCount(deviceKpi);
  const petSuccessCount = summarizePetSuccessCount(deviceKpi);
  const dailyFlowSuccessCount = summarizeDailyFlowSuccessCount(deviceKpi);
  const healthFlowSuccessCount = summarizeHealthFlowSuccessCount(deviceKpi);
  const receiptFlowCount = summarizeReceiptFlowCount(deviceKpi);
  const socialSuccessCount = summarizeSocialSuccessCount(deviceKpi);
  const chatSuccessCount = summarizeChatSuccessCount(deviceKpi);
  const communitySuccessCount = summarizeCommunitySuccessCount(deviceKpi);
  const carePlanSuccessCount = summarizeCarePlanSuccessCount(deviceKpi);
  const rideSuccessCount = summarizeRideSuccessCount(deviceKpi);
  const drilldownTarget = buildDeviceKpiDrilldownTarget(deviceKpi);

  return {
    deviceInstallId: deviceKpi.deviceInstallId,
    deviceInstallIdLabel: toDeviceInstallIdLabel(deviceKpi.deviceInstallId),
    platformLabel: toPlatformLabel(deviceKpi.platform),
    appVersionLabel: toLabel(deviceKpi.appVersion),
    releaseLabel: toLabel(deviceKpi.release),
    lastSeenAtLabel: formatAdminDate(deviceKpi.lastSeenAt),
    latestUserIdHashLabel: toLabel(deviceKpi.latestUserIdHash),
    latestUserAccountIdentityLabel: formatAdminAccountIdentityLabel(deviceKpi.latestUserAccountIdentity, locale),
    latestUserProviderIdentityLabel: formatAdminProviderIdentityLabel(deviceKpi.latestUserAccountIdentity, locale),
    latestUserLinkedProviderCountLabel: formatAdminLinkedProviderCountLabel(
      deviceKpi.latestUserAccountIdentity,
      locale,
    ),
    latestRuntimeAppBuildNumberLabel: formatAdminTelemetryAppBuildNumberLabel(
      deviceKpi.latestRuntimeAppBuildNumber,
      locale,
    ),
    latestRuntimeLocaleLabel: formatAdminTelemetryLocaleLabel(deviceKpi.latestRuntimeLocale, locale),
    latestRuntimeTimezoneOffsetBucketLabel: formatAdminTelemetryTimezoneOffsetBucketLabel(
      deviceKpi.latestRuntimeTimezoneOffsetBucket,
      locale,
    ),
    latestRuntimeTimezoneNameBucketLabel: formatAdminTelemetryTimezoneNameBucketLabel(
      deviceKpi.latestRuntimeTimezoneNameBucket,
      locale,
    ),
    latestRuntimeDeviceModelBucketLabel: formatAdminTelemetryDeviceModelBucketLabel(
      deviceKpi.latestRuntimeDeviceModelBucket,
      locale,
    ),
    latestRuntimeDeviceModelNameBucketLabel: formatAdminTelemetryDeviceModelNameBucketLabel(
      deviceKpi.latestRuntimeDeviceModelNameBucket,
      locale,
    ),
    latestRuntimeOsMajorBucketLabel: formatAdminTelemetryOsMajorBucketLabel(
      deviceKpi.latestRuntimeOsMajorBucket,
      locale,
    ),
    latestRuntimeOsVersionMinorBucketLabel: formatAdminTelemetryOsVersionMinorBucketLabel(
      deviceKpi.latestRuntimeOsVersionMinorBucket,
      locale,
    ),
    latestRuntimeNetworkTypeBucketLabel: formatAdminTelemetryNetworkTypeBucketLabel(
      deviceKpi.latestRuntimeNetworkTypeBucket,
      locale,
    ),
    latestRuntimePhysicalDeviceBucketLabel: formatAdminTelemetryPhysicalDeviceBucketLabel(
      deviceKpi.latestRuntimePhysicalDeviceBucket,
      locale,
    ),
    latestRuntimeSummaryLabel: formatAdminTelemetryRuntimeSummaryLabel(
      {
        appBuildNumber: deviceKpi.latestRuntimeAppBuildNumber,
        deviceModelNameBucket: deviceKpi.latestRuntimeDeviceModelNameBucket,
        osVersionMinorBucket: deviceKpi.latestRuntimeOsVersionMinorBucket,
        networkTypeBucket: deviceKpi.latestRuntimeNetworkTypeBucket,
        physicalDeviceBucket: deviceKpi.latestRuntimePhysicalDeviceBucket,
      },
      locale,
    ),
    latestRuntimeContextAtLabel: toOptionalDateLabel(deviceKpi.latestRuntimeContextAt),
    sessionStartCountLabel: formatAdminCount(deviceKpi.sessionStartCount),
    authSuccessCountLabel: formatAdminCount(deviceKpi.authSuccessCount),
    petCreateSuccessCountLabel: formatAdminCount(deviceKpi.petCreateSuccessCount),
    petUpdateSuccessCountLabel: formatAdminCount(deviceKpi.petUpdateSuccessCount),
    petDeleteSuccessCountLabel: formatAdminCount(deviceKpi.petDeleteSuccessCount),
    petPhotoConfirmSuccessCountLabel: formatAdminCount(deviceKpi.petPhotoConfirmSuccessCount),
    petSuccessCountLabel: formatAdminCount(petSuccessCount),
    todayStatusSaveSuccessCountLabel: formatAdminCount(deviceKpi.todayStatusSaveSuccessCount),
    dailySpeechCaptureStartCountLabel: formatAdminCount(deviceKpi.dailySpeechCaptureStartCount),
    dailySpeechCaptureCancelCountLabel: formatAdminCount(deviceKpi.dailySpeechCaptureCancelCount),
    dailyDraftCreateSuccessCountLabel: formatAdminCount(deviceKpi.dailyDraftCreateSuccessCount),
    dailyDraftUpdateCountLabel: formatAdminCount(deviceKpi.dailyDraftUpdateCount),
    dailyDraftDiscardCountLabel: formatAdminCount(deviceKpi.dailyDraftDiscardCount),
    dailyRecordSaveSuccessCountLabel: formatAdminCount(deviceKpi.dailyRecordSaveSuccessCount),
    dailyMediaUpdateSuccessCountLabel: formatAdminCount(deviceKpi.dailyMediaUpdateSuccessCount),
    dailyFlowSuccessCountLabel: formatAdminCount(dailyFlowSuccessCount),
    healthFileUploadSuccessCountLabel: formatAdminCount(deviceKpi.healthFileUploadSuccessCount),
    healthFileConfirmSuccessCountLabel: formatAdminCount(deviceKpi.healthFileConfirmSuccessCount),
    healthAnalysisStartCountLabel: formatAdminCount(deviceKpi.healthAnalysisStartCount),
    healthFlowSuccessCountLabel: formatAdminCount(healthFlowSuccessCount),
    healthRecordSaveSuccessCountLabel: formatAdminCount(deviceKpi.healthRecordSaveSuccessCount),
    receiptExtractCountLabel: formatAdminCount(deviceKpi.receiptExtractCount),
    receiptSaveSuccessCountLabel: formatAdminCount(deviceKpi.receiptSaveSuccessCount),
    receiptCategoryCreateSuccessCountLabel: formatAdminCount(deviceKpi.receiptCategoryCreateSuccessCount),
    receiptCategoryUpdateSuccessCountLabel: formatAdminCount(deviceKpi.receiptCategoryUpdateSuccessCount),
    receiptCategoryDeleteSuccessCountLabel: formatAdminCount(deviceKpi.receiptCategoryDeleteSuccessCount),
    receiptFlowCountLabel: formatAdminCount(receiptFlowCount),
    socialSuccessCountLabel: formatAdminCount(socialSuccessCount),
    chatSuccessCountLabel: formatAdminCount(chatSuccessCount),
    communitySuccessCountLabel: formatAdminCount(communitySuccessCount),
    carePlanSuccessCountLabel: formatAdminCount(carePlanSuccessCount),
    rideRequestSuccessCountLabel: formatAdminCount(rideSuccessCount),
    totalSuccessCountLabel: formatAdminCount(totalSuccessCount),
    failureCountLabel: formatAdminCount(deviceKpi.failureCount),
    topErrorCodeLabel: toLabel(deviceKpi.topErrorCode),
    dailyContextCue: {
      speechDurationBucketLabel: toLabel(deviceKpi.latestDailySpeechDurationBucket),
      speechDurationBucketObservedAtLabel: toOptionalDateLabel(deviceKpi.latestDailySpeechDurationBucketAt),
      draftEditCountBucketLabel: toLabel(deviceKpi.latestDailyDraftEditCountBucket),
      draftEditCountBucketObservedAtLabel: toOptionalDateLabel(deviceKpi.latestDailyDraftEditCountBucketAt),
      draftDiscardReasonCategoryLabel: toLabel(deviceKpi.latestDailyDraftDiscardReasonCategory),
      draftDiscardReasonCategoryObservedAtLabel: toOptionalDateLabel(
        deviceKpi.latestDailyDraftDiscardReasonCategoryAt,
      ),
      hasAnyContext: Boolean(
        deviceKpi.latestDailySpeechDurationBucket ||
          deviceKpi.latestDailyDraftEditCountBucket ||
          deviceKpi.latestDailyDraftDiscardReasonCategory,
      ),
    },
    petContextCue: {
      profileCompletionStepLabel: formatAdminPetProfileCompletionStepLabel(
        deviceKpi.latestPetProfileCompletionStep,
        locale,
      ),
      attemptCountBucketLabel: formatAdminPetAttemptCountBucketLabel(
        deviceKpi.latestPetAttemptCountBucket,
        locale,
      ),
      speciesBucketLabel: formatAdminPetSpeciesBucketLabel(deviceKpi.latestPetSpeciesBucket, locale),
      breedSelectionBucketLabel: formatAdminPetBreedSelectionBucketLabel(
        deviceKpi.latestPetBreedSelectionBucket,
        locale,
      ),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestPetContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestPetProfileCompletionStep ||
          deviceKpi.latestPetAttemptCountBucket ||
        deviceKpi.latestPetSpeciesBucket ||
          deviceKpi.latestPetBreedSelectionBucket,
      ),
    },
    socialContextCue: {
      targetUserLookupKindLabel: formatAdminSocialTargetUserLookupKindLabel(
        deviceKpi.latestSocialTargetUserLookupKind,
        locale,
      ),
      publicPetCountBucketLabel: formatAdminSocialPublicPetCountBucketLabel(
        deviceKpi.latestSocialPublicPetCountBucket,
        locale,
      ),
      profileMutationFieldSetBucketLabel:
        formatAdminSocialProfileMutationFieldSetBucketLabel(
          deviceKpi.latestSocialProfileMutationFieldSetBucket,
          locale,
        ),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestSocialContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestSocialTargetUserLookupKind ||
          deviceKpi.latestSocialPublicPetCountBucket ||
          deviceKpi.latestSocialProfileMutationFieldSetBucket,
      ),
    },
    chatContextCue: {
      roomKindLabel: formatAdminChatRoomKindLabel(deviceKpi.latestChatRoomKind, locale),
      messageLengthBucketLabel: formatAdminChatMessageLengthBucketLabel(
        deviceKpi.latestChatMessageLengthBucket,
        locale,
      ),
      membershipCountBucketLabel: formatAdminChatMembershipCountBucketLabel(
        deviceKpi.latestChatMembershipCountBucket,
        locale,
      ),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestChatContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestChatRoomKind ||
          deviceKpi.latestChatMessageLengthBucket ||
          deviceKpi.latestChatMembershipCountBucket,
      ),
    },
    receiptContextCue: {
      extractSourceBucketLabel: formatAdminReceiptExtractSourceBucketLabel(
        deviceKpi.latestReceiptExtractSourceBucket,
        locale,
      ),
      imageByteSizeBucketLabel: formatAdminReceiptImageByteSizeBucketLabel(
        deviceKpi.latestReceiptImageByteSizeBucket,
        locale,
      ),
      extractionConfidenceBucketLabel: formatAdminReceiptExtractionConfidenceBucketLabel(
        deviceKpi.latestReceiptExtractionConfidenceBucket,
        locale,
      ),
      manualOverrideLabel: formatAdminBooleanLabel(deviceKpi.latestReceiptManualOverride, locale),
      categoryMutationTypeLabel: formatAdminReceiptCategoryMutationTypeLabel(
        deviceKpi.latestReceiptCategoryMutationType,
        locale,
      ),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestReceiptContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestReceiptExtractSourceBucket ||
          deviceKpi.latestReceiptImageByteSizeBucket ||
          deviceKpi.latestReceiptExtractionConfidenceBucket ||
          typeof deviceKpi.latestReceiptManualOverride === "boolean" ||
        deviceKpi.latestReceiptCategoryMutationType,
      ),
    },
    carePlanContextCue: {
      scheduleRecurrenceBucketLabel: formatAdminCarePlanRecurrenceBucketLabel(
        deviceKpi.latestCarePlanScheduleRecurrenceBucket,
        locale,
      ),
      dosePresenceLabel: formatAdminCarePlanDosePresenceLabel(
        deviceKpi.latestCarePlanDosePresence,
        locale,
      ),
      occurrenceActionKindLabel: formatAdminCareOccurrenceResultKindLabel(
        deviceKpi.latestCarePlanOccurrenceActionKind,
        locale,
      ),
      snoozeWindowBucketLabel: formatAdminCareOccurrenceSnoozeWindowBucketLabel(
        deviceKpi.latestCarePlanSnoozeWindowBucket,
        locale,
      ),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestCarePlanContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestCarePlanScheduleRecurrenceBucket ||
          deviceKpi.latestCarePlanDosePresence ||
          deviceKpi.latestCarePlanOccurrenceActionKind ||
          deviceKpi.latestCarePlanSnoozeWindowBucket,
      ),
    },
    rideContextCue: {
      visitLeadTimeBucketLabel: formatAdminRideVisitLeadTimeBucketLabel(
        deviceKpi.latestRideVisitLeadTimeBucket,
        locale,
      ),
      longestAddressLengthBucketLabel: formatAdminRideLongestAddressLengthBucketLabel(
        deviceKpi.latestRideLongestAddressLengthBucket,
        locale,
      ),
      memoProvidedLabel: formatAdminBooleanLabel(deviceKpi.latestRideMemoProvided, locale),
      observedAtLabel: toOptionalDateLabel(deviceKpi.latestRideContextAt),
      hasAnyContext: Boolean(
        deviceKpi.latestRideVisitLeadTimeBucket ||
          deviceKpi.latestRideLongestAddressLengthBucket ||
          typeof deviceKpi.latestRideMemoProvided === "boolean",
      ),
    },
    drilldownTarget,
    scenarioPresets: createDeviceKpiScenarioPresets(deviceKpi, drilldownTarget, locale),
  };
}

function toDeviceKpiRowViewModel(
  deviceKpi: AdminDeviceKpi,
  locale: AdminDashboardLocale,
): DeviceKpiRowViewModel {
  const detail = toDeviceKpiDetailViewModel(deviceKpi, locale);

  return {
    id: `${deviceKpi.deviceInstallId}:${deviceKpi.platform}:${deviceKpi.appVersion ?? "-"}:${deviceKpi.release ?? "-"}`,
    deviceInstallId: deviceKpi.deviceInstallId,
    platform: deviceKpi.platform,
    appVersion: deviceKpi.appVersion,
    release: deviceKpi.release,
    lastSeenAt: deviceKpi.lastSeenAt,
    sessionStartCount: deviceKpi.sessionStartCount,
    failureCount: deviceKpi.failureCount,
    deviceInstallIdLabel: detail.deviceInstallIdLabel,
    platformLabel: detail.platformLabel,
    appVersionLabel: detail.appVersionLabel,
    releaseLabel: detail.releaseLabel,
    lastSeenAtLabel: detail.lastSeenAtLabel,
    sessionStartCountLabel: detail.sessionStartCountLabel,
    authSuccessCountLabel: detail.authSuccessCountLabel,
    petSuccessCountLabel: detail.petSuccessCountLabel,
    todayStatusSaveSuccessCountLabel: detail.todayStatusSaveSuccessCountLabel,
    dailySpeechCaptureStartCountLabel: detail.dailySpeechCaptureStartCountLabel,
    dailySpeechCaptureCancelCountLabel: detail.dailySpeechCaptureCancelCountLabel,
    dailyFlowSuccessCountLabel: detail.dailyFlowSuccessCountLabel,
    healthFlowSuccessCountLabel: detail.healthFlowSuccessCountLabel,
    healthRecordSaveSuccessCountLabel: detail.healthRecordSaveSuccessCountLabel,
    receiptFlowCountLabel: detail.receiptFlowCountLabel,
    receiptSaveSuccessCountLabel: detail.receiptSaveSuccessCountLabel,
    socialSuccessCountLabel: detail.socialSuccessCountLabel,
    chatSuccessCountLabel: detail.chatSuccessCountLabel,
    communitySuccessCountLabel: detail.communitySuccessCountLabel,
    carePlanSuccessCountLabel: detail.carePlanSuccessCountLabel,
    rideRequestSuccessCountLabel: detail.rideRequestSuccessCountLabel,
    totalSuccessCountLabel: detail.totalSuccessCountLabel,
    failureCountLabel: detail.failureCountLabel,
    topErrorCodeLabel: detail.topErrorCodeLabel,
    latestUserIdHashLabel: detail.latestUserIdHashLabel,
    latestUserAccountIdentityLabel: detail.latestUserAccountIdentityLabel,
    latestUserProviderIdentityLabel: detail.latestUserProviderIdentityLabel,
    latestUserLinkedProviderCountLabel: detail.latestUserLinkedProviderCountLabel,
    latestRuntimeSummaryLabel: detail.latestRuntimeSummaryLabel,
    drilldownTarget: detail.drilldownTarget,
    detail,
  };
}

export function toDeviceKpisViewModel(
  page: AdminDeviceKpiPage,
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale = "ko",
  projectionGapEvidence?: DeviceKpiProjectionGapEvidenceViewModel,
  projectionGapRows: DeviceKpiProjectionGapRowViewModel[] = [],
  baselineObservedAtOverride?: string,
  latestSourceActivityAtOverride?: string,
): DeviceKpisViewModel {
  const sortedItems = [...page.items].sort(compareDeviceKpisByLastSeenAt);
  const totalFailureCount = page.items.reduce((sum, item) => sum + item.failureCount, 0);
  const totalSessionCount = page.items.reduce((sum, item) => sum + item.sessionStartCount, 0);
  const baselineObservedAt = baselineObservedAtOverride ?? [...page.items].sort(compareDeviceKpisByLastSeenAt)[0]?.lastSeenAt;
  const latestObservedAt = sortedItems[0]?.lastSeenAt;
  const latestSourceActivityAt = latestSourceActivityAtOverride ?? projectionGapRows[0]?.latestFailedFlowAt;
  const latestObservedLagging = isDeviceKpiObservationLagging(latestObservedAt, meta.generatedAt);
  const baselineObservedLagging = isDeviceKpiObservationLagging(baselineObservedAt, meta.generatedAt);

  return {
    totalCountLabel: formatAdminCount(page.totalCount ?? page.items.length),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    nextCursor: meta.nextCursor,
    selectedScopeLabel: buildSelectedScopeLabel(sortedItems, locale),
    baselineObservedAtLabel: toOptionalDateLabel(baselineObservedAt),
    latestObservedAtLabel: toOptionalDateLabel(latestObservedAt),
    latestSourceActivityAtLabel: toOptionalDateLabel(latestSourceActivityAt),
    latestObservedLagging,
    observationLagLabel: formatObservationLagLabel(baselineObservedAt, meta.generatedAt, locale),
    freshnessAlignment: deriveFreshnessAlignment(baselineObservedLagging, meta.sourceFreshness),
    distinctUserCountLabel: countDistinctUsers(page.items.map((item) => item.latestUserIdHash)),
    totalFailureCountLabel: formatAdminCount(totalFailureCount),
    totalSessionCountLabel: formatAdminCount(totalSessionCount),
    projectionGapRowCountLabel: formatAdminCount(projectionGapRows.length),
    projectionGapEvidence,
    projectionGapRows,
    rows: sortedItems.map((item) => toDeviceKpiRowViewModel(item, locale)),
    sourceFreshness: Object.entries(meta.sourceFreshness)
      .sort(([leftSource], [rightSource]) => leftSource.localeCompare(rightSource))
      .map(([source, freshness]) => ({
        source,
        freshness,
        sourceKind: meta.sourceKinds?.[source],
        sourceKindLabel: formatAdminSourceKindLabel(meta.sourceKinds?.[source], locale),
      })),
  };
}
